import { OrganizationService } from "@/gen/omnios/v1/organization_connect.js";
import type { Client } from "@connectrpc/connect";
import { createClient } from "@connectrpc/connect";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { transport } from "./grpc";
import { useWorkspaceStore } from "./workspace";

export interface Organization {
	id: string;
	name: string;
	slug: string;
	reportingCurrency: string;
	fiscalYearStart: number;
}

interface OrganizationState {
	organizations: Organization[];
	selectedOrgId: string | null;
	allEntities: boolean;
	isLoading: boolean;
	selected: () => Organization | undefined;
	setSelectedOrg: (id: string) => void;
	setAllEntities: (val: boolean) => void;
	entityIdsForCurrentOrg: () => string[];
	fetchOrganizations: () => Promise<void>;
}

let _client: Client<typeof OrganizationService> | null = null;
function getClient() {
	if (!_client) _client = createClient(OrganizationService, transport);
	return _client;
}

export const useOrganizationStore = create<OrganizationState>()(
	persist(
		(set, get) => ({
			organizations: [],
			selectedOrgId: null,
			allEntities: false,
			isLoading: false,

			selected: () => {
				const { organizations, selectedOrgId } = get();
				return organizations.find((o) => o.id === selectedOrgId);
			},

			setSelectedOrg: (id: string) => {
				set({ selectedOrgId: id });
			},

			setAllEntities: (val: boolean) => {
				set({ allEntities: val });
			},

			entityIdsForCurrentOrg: () => {
				const { selectedOrgId } = get();
				if (!selectedOrgId) return [];
				return useWorkspaceStore
					.getState()
					.workspaces.filter((w) => w.organizationId === selectedOrgId)
					.map((w) => w.id);
			},

			fetchOrganizations: async () => {
				set({ isLoading: true });
				try {
					const resp = await getClient().listOrganizations({});
					const organizations = resp.organizations.map((o) => ({
						id: o.id,
						name: o.name,
						slug: o.slug,
						reportingCurrency: o.reportingCurrency,
						fiscalYearStart: o.fiscalYearStart,
					}));
					const { selectedOrgId } = get();
					const still = organizations.find((o) => o.id === selectedOrgId);
					set({
						organizations,
						isLoading: false,
						selectedOrgId: still
							? selectedOrgId
							: (organizations[0]?.id ?? null),
					});
				} catch {
					set({ isLoading: false });
				}
			},
		}),
		{
			name: "omnios-org",
			partialize: (state) => ({
				selectedOrgId: state.selectedOrgId,
				allEntities: state.allEntities,
			}),
		},
	),
);
